import { BaseUser } from '@abaco/web-common/src/BaseUser';
import { AxiosPromise, AxiosRequestConfig, AxiosResponse, HTTPClient } from '@abaco/web-common/src/HTTP';
import jwtDecode from "jwt-decode";
import { BASE, SITI_AGRI } from './models/Constants';
import { ContextFunctions, FunctionResponse } from './models/FunctionResponse';
import { LoginResponse } from './models/LoginResponse';
import { PrivacyPreferenceRequest, PrivacyResponse } from './models/Privacy';
import { RefreshTokensResponse } from './models/RefreshTokensResponse';
import SsoVuexModule from './state';

export interface Messages
{
    warnings: Message[];
    errors: Message[];
}

export interface Message
{
    key: string;
    values: string[];
}

export type LoginCallback = (user: BaseUser) => void;
export type BasicLoginCallback = (http: HTTPClient) => Promise<LoginResponse>;

interface AuthToken
{
    sub: string;
    iat: number;
    exp: number;
}

interface MyStorage
{
    getRefreshToken(): string | null;
    setRefreshToken(val: string | null): void;

    getAuthToken(): string | null;
    setAuthToken(val: string | null): void;

    getUser(): BaseUser | null;
    setUser(val: BaseUser | null): void;

    clear(): void;
}

class SessionStore implements MyStorage
{
    private setOrRemove(key: string, val: string | null)
    {
        if (val)
        {
            sessionStorage.setItem(key, val);
        } else
        {
            sessionStorage.removeItem(key);
        }
    }

    getRefreshToken(): string | null
    {
        return sessionStorage.getItem("rt");
    }

    setRefreshToken(val: string | null): void
    {
        this.setOrRemove("rt", val);
    }

    getAuthToken(): string | null
    {
        return sessionStorage.getItem("at");
    }

    setAuthToken(val: string | null): void
    {
        this.setOrRemove("at", val);
    }

    getUser(): BaseUser | null
    {
        const str = sessionStorage.getItem("user");
        return str ? JSON.parse(str) : null;
    }

    setUser(val: BaseUser | null): void
    {
        this.setOrRemove("user", val ? JSON.stringify(val) : null);
    }

    clear()
    {
        sessionStorage.removeItem("rt");
        sessionStorage.removeItem("at");
        sessionStorage.removeItem("user");
    }
}

export default class TokensResources
{
    private configProviderAdded = false;
    private refreshClient = new HTTPClient();
    private inFlightRefreshRequest: AxiosPromise<RefreshTokensResponse> | null = null;
    protected authTokenExpiresAt = 0;

    constructor(private http: HTTPClient, private state: SsoVuexModule, private onLogin?: LoginCallback, private session: MyStorage = new SessionStore(), private skipPrivacyCheck = false, private useSSO1 = false, private basicAuth?: BasicLoginCallback)
    {
    }

    private async onLoginResponse(res: LoginResponse): Promise<Messages>
    {
        const messages = {
            warnings: res.warnings,
            errors: res.errors
        };

        if (res.errors.length > 0 || !res.auth_token)
        {
            return messages
        }

        this.parseToken(res.auth_token, res.user_id);
        this.session.setAuthToken(res.auth_token);
        this.session.setRefreshToken(res.refresh_token);

        this.addConfigProvider();


        this.onSuccessfulLogin({
            dateFormat: res.dateFormat,
            description: res.description || res.user_id,
            locale: res.locale,
            username: res.user_id.toUpperCase(),
            isBasic: true
        });

        // if (!this.skipPrivacyCheck)
        // {
        //     this.state.privacyChecks = await this.checkPrivacyPreferences();
        // }

        await this.loadUserFunctions();

        return messages;
    }

    private async basicLoginResponse(res: LoginResponse, username: string, password: string): Promise<Messages>
    {
        const messages = {
            warnings: res.warnings,
            errors: res.errors
        };

        if (res.errors.length > 0)
        {
            return messages
        }

        res.auth_token = btoa(`${username}:${password}`);

        this.session.setAuthToken(res.auth_token);

        this.onSuccessfulLogin({
            dateFormat: res.dateFormat,
            description: res.description,
            locale: res.locale,
            username: res.user_id,
            isBasic: false
        });
        return { warnings: [], errors: [] }
    }

    public async login(username: string, password: string): Promise<Messages>
    {
        if (this.basicAuth)
        {
            this.http.addRequestConfigProvider(async (config) =>
            {
                config.auth = { username, password };
            })

            const loginResponse = await this.basicAuth(this.http);
            return this.basicLoginResponse(loginResponse, username, password)
        }
        const httpRes = await this.http.post<LoginResponse>(`${BASE}/login`, { username, password });
        return this.onLoginResponse(httpRes.data);
    }

    public async exchangeSso1Token(ssoToken: string, loginContext: string)
    {
        const httpRes = await this.http.post<LoginResponse>(`${BASE}/exchange-sso1`, { ssoToken, loginContext });
        return this.onLoginResponse(httpRes.data);
    }

    private onSuccessfulLogin(user: BaseUser)
    {
        this.session.setUser(user);
        this.state.user = user;
        if (this.onLogin)
        {
            this.onLogin(this.state.user);
        }
    }

    private addConfigProvider()
    {
        if (!this.configProviderAdded)
        {
            this.http.addRequestConfigProvider((config) => this.provideConfig(config));
            this.configProviderAdded = true;
        }
    }

    public resumeSession(): boolean
    {
        const user = this.session.getUser();
        if (!user)
        {
            return false;
        }

        this.onSuccessfulLogin(user);

        const token = this.session.getAuthToken();
        if (!token)
        {
            return false;
        }

        if (this.basicAuth)
        {
            this.http.addRequestConfigProvider(async (config) =>
            {
                config.headers = { ...config.headers, Authorization: `Basic ${token}` }
            })
            return true;
        }

        this.parseToken(token, user.username);
        this.addConfigProvider();
        return true;
    }

    public logout()
    {
        this.session.clear();
        this.authTokenExpiresAt = 0;
        this.state.user = null;
    }

    // private async isBasicUser()
    // {
    //     const url = "/sitiagri-rest-api/api_basic/v1/user_registry/who_am_i";
    //     try
    //     {
    //         const res = await this.http.get(url);
    //         return res.data.is_basic;
    //     } catch (error)
    //     {
    //         return false;
    //     }
    // }

    private async checkPrivacyPreferences(): Promise<PrivacyResponse[]>
    {
        const res: Promise<AxiosResponse<{ result: boolean }>>[] = [];

        res.push(this.http.get(`${SITI_AGRI}/user_registry/module_competencies/PORTAL/ACCEPT_PRIVACY_1`));
        res.push(this.http.get(`${SITI_AGRI}/user_registry/module_competencies/PORTAL/ACCEPT_PRIVACY_2`));

        const responses = (await Promise.all(res)).map(r => r.data);
        return responses.map((r, i) => ({
            module: `ACCEPT_PRIVACY_${i + 1}`,
            result: r.result
        }))
    }

    public async sendPrivacyPreferences(privacyPreferences: PrivacyPreferenceRequest[]): Promise<PrivacyResponse[]>
    {
        const res: Promise<AxiosResponse>[] = [];

        privacyPreferences.forEach(p =>
        {
            res.push(this.http.post(`${SITI_AGRI}/user_registry/module_competencies/`, p))
        })

        return (await Promise.all(res)).map(r => r.data);
    }

    public async loadUserFunctions()
    {
        const res = await this.http.get<FunctionResponse[]>(`${BASE}/user-functions`);
        const functions = res.data || [];
        this.state.userFunctions = functions.reduce((accum, f) =>
        {
            const cur = accum[f.context_id];
            if (cur)
            {
                cur.push(f.function_name);
            } else
            {
                accum[f.context_id] = [f.function_name];
            }
            return accum;
        }, {} as ContextFunctions);
    }

    // XXX: da chiamare
    public async loadUserContexts(): Promise<string[]>
    {
        const res = await this.http.get<string[]>(`${BASE}/user-contexts`);
        return res.data || [];
    }

    // For testing
    protected parseToken(authToken: string, expectedUser?: string): string
    {
        const decoded = jwtDecode(authToken) as AuthToken;
        if (!decoded.sub || (expectedUser && expectedUser.toUpperCase() !== decoded.sub.toUpperCase()))
        {
            this.session.clear();
            this.authTokenExpiresAt = 0;
            throw new Error("Bad subject");
        }

        // Adjust the expiration time based on the possbile time difference between sever and client
        const serverNow = new Date(decoded.iat * 1000);
        const now = new Date();
        const deltaMs = (serverNow.getTime() - now.getTime());

        this.authTokenExpiresAt = ((decoded.exp - 20) * 1000) - Math.abs(deltaMs);// Remove some seconds to have margin on expire time
        return decoded.sub;
    }

    private async provideConfig(config: AxiosRequestConfig)
    {
        const authToken = this.session.getAuthToken();
        if (!authToken)
        {
            return;
        }

        const refreshed = await this.refreshTokens();
        if (!refreshed)
        {
            return;
        }

        if (this.useSSO1)
        {
            config.params = config.params || {};
            if (!config.params.SSO_TOKEN)
            {
                config.params.SSO_TOKEN = await this.getSSOToken();
            }
        }

        config.headers = config.headers || {};
        config.headers["Authorization"] = `JWT ${this.session.getAuthToken()}`;
    }

    public async getSSOToken(prefix = true): Promise<string>
    {
        let authToken = this.session.getAuthToken();
        if (!authToken)
        {
            throw new Error("Not authenticated");
        }

        const refreshed = await this.refreshTokens();
        authToken = refreshed ? this.session.getAuthToken() : null;
        if (!authToken)
        {
            throw new Error("No auth token found");
        }

        return prefix ? `j2_${authToken}` : authToken;
    }

    public getAuthTokenExpiresAt(): number
    {
        return this.authTokenExpiresAt;
    }

    // For testing
    protected callRefreshToken(): AxiosPromise<RefreshTokensResponse>
    {
        return this.refreshClient.post(`${BASE}/refresh`, { "refresh_token": this.session.getRefreshToken() });
    }

    // For testing
    protected async refreshTokens(): Promise<boolean>
    {
        if (!this.session.getRefreshToken())
        {
            return false;
        }

        const now = new Date();
        if (this.authTokenExpiresAt > now.getTime())
        {
            return true;
        }

        let ret = true, createdByMe = false;
        try
        {
            if (!this.inFlightRefreshRequest)
            {
                this.inFlightRefreshRequest = this.callRefreshToken();
                createdByMe = true;
            }

            /* TOM: I think that a local copy is not really needed
             *      but am not 100% sure that the assignment: this.inFlightRefreshRequest = null;
             *      that I do later will not create issues on the ES5 polyfilled and transpiled javascript
             *      so I copy the variable, just in case...
             */
            const localCopy = this.inFlightRefreshRequest;
            const ares = await localCopy;
            const res = ares.data;
            this.parseToken(res.auth_token, this.state.user ? this.state.user.username : undefined);
            this.session.setAuthToken(res.auth_token);
            this.session.setRefreshToken(res.refresh_token);

            if (!this.state.user)
            {
                // If we are here, it means that the session is still valid but we lost hte javascript state
                // I.e. a user pressed refresh on the web browser
                const user = this.session.getUser();
                if (user)
                {
                    this.onSuccessfulLogin(user);
                }
            }
        } catch (err)
        {
            this.session.clear();
            this.authTokenExpiresAt = 0;
            this.state.user = null;

            ret = false;
        }

        if (createdByMe)
        {
            this.inFlightRefreshRequest = null;
        }

        return ret;
    }
}
